// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import Minus from "@adl/XDL/Icons/Minus";
import Plus from "@adl/XDL/Icons/Plus";
import { xIsAuthenticated, xScreen } from "@adl/XDL/Utilities";
import { color, mergeWithCss, xMask } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: ProductQuantityOrderedStyles;
    showActions?: boolean;
    showLabel?: boolean;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAuthenticated: xIsAuthenticated(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

// XNG-Changes: Styles & Structure
export interface ProductQuantityOrderedActionsStyles {
    wrapper?: InjectableCss;
    actionClickable?: ClickableProps;
    quantityOrderedTextField?: TextFieldPresentationProps;
}
export interface ProductQuantityOrderedStyles extends TextFieldPresentationProps {
    actionsStyles?: ProductQuantityOrderedActionsStyles;
}

export const productQuantityOrderedStyles: ProductQuantityOrderedStyles = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
            width: auto;
        `,
    },
    actionsStyles: {
        actionClickable: {
            css: css`
                height: 26px;
                padding: 0 8px;
                display: flex;
                align-items: center;
                transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

                ${IconWrapper} {
                    color: ${color("common.ui5")};
                }
                &:hover {
                    background: ${color("common.ui5")};
                    ${IconWrapper} {
                        color: ${color("common.white")};
                    }
                }
                &:focus {
                    outline: none;
                }
            `,
        },
        wrapper: {
            css: css`
                background: ${color("common.white")};
                border: 1px solid ${color("common.border")};
                border-radius: 2px;
                display: flex;
                align-items: center;
                max-width: 102px;
                height: 30px;
                &:hover {
                    border: 1px solid ${color("common.ui1")};
                }
                &:focus-within {
                    border: 1px solid ${color("common.ui2")};
                }
            `,
        },
        quantityOrderedTextField: {
            cssOverrides: {
                inputSelect: css`
                    border: none;
                    font-size: 14px;
                    letter-spacing: 0;
                    line-height: 16px;
                    text-align: center;
                    color: ${color("text")};
                    height: 28px;
                    box-shadow: none !important;
                    &:hover,
                    &:focus {
                        border: none;
                        box-shadow: none !important;
                    }
                `,
            },
        },
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    showActions,
    showLabel,
    isAuthenticated,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    if ((!hasEnoughInventory && !failedToLoadInventory) || (!canAddToCart && isAuthenticated)) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isAuthenticated) {
            return;
        }
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    // XNG-Changes: apply qty mask for value
    const xChangeHandler = (event: any) => {
        event.target.value = Number(xMask.qty(event.target.value));
        qtyChangeHandler(event);
    };

    const updateQtyByAction = (type: "increase" | "decrease") => {
        const event = { target: { value: "" } };
        event.target.value = `${Number(productQty) + (type === "increase" ? 1 : -1)}`;
        xChangeHandler(event);
    };
    const textFieldId = `product-qty-orderd-input-${productId}`;
    const qtyInputProps = {
        label: showLabel && (labelOverride ?? translate("QTY")),
        value: productQty,
        type: "text",
        min: 0,
        max: 999999,
        "data-test-selector": "product_qtyOrdered",
        id: textFieldId,
        ...otherProps,
        onKeyPress: keyPressHandler,
        onChange: xChangeHandler,
    };

    if (showActions) {
        return (
            <StyledWrapper {...styles.actionsStyles?.wrapper}>
                <Clickable
                    {...styles.actionsStyles?.actionClickable}
                    disabled={Number(productQty) <= 1 || !isAuthenticated}
                    onClick={() => updateQtyByAction("decrease")}
                    data-test-selector="product_qtyOrdered_decrease"
                    aria-label="decrease quantity"
                >
                    <Icon src={Minus} size={10} />
                </Clickable>
                <VisuallyHidden as="label" htmlFor={otherProps.id || textFieldId}>
                    {translate("QTY")}
                </VisuallyHidden>
                <TextField {...qtyInputProps} {...styles.actionsStyles?.quantityOrderedTextField} />
                <Clickable
                    {...styles.actionsStyles?.actionClickable}
                    disabled={Number(productQty) > 999999 || !isAuthenticated}
                    onClick={() => updateQtyByAction("increase")}
                    data-test-selector="product_qtyOrdered_increase"
                    aria-label="increase quantity"
                >
                    <Icon src={Plus} size={10} />
                </Clickable>
            </StyledWrapper>
        );
    }
    const { actionsStyles, ...textFieldStyles } = styles;
    return (
        <>
            <VisuallyHidden as="label" htmlFor={otherProps.id || textFieldId}>
                {translate("QTY")}
            </VisuallyHidden>
            <TextField {...qtyInputProps} {...textFieldStyles} />
        </>
    );
};

ProductQuantityOrdered.defaultProps = {
    showLabel: true,
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
